import { zodResolver } from '@hookform/resolvers/zod'
import { Add, ArrowRight } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Chip, ChipSize, ChipVariant } from '@/components/Chip/Chip'
import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import { useRunReportWizardStep } from '@/pages/RunReport/hooks/useRunReportWizardStep'
import { ERunReportStep, ESearchType } from '@/pages/RunReport/types'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import { setFirstStep } from '@/store/api/RunReport/RunReport.slice'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'
import { behaviorFlagsSchema } from '@/utils/schemas'
import type { behaviorFlagsSchemaType } from '@/utils/schemas'

import { BehaviorDetectionGuideSheet } from '../../components/BehaviorDetectionGuideSheet'
import { FocusedTopicsGuideSheet } from '../../components/FocusedTopicsGuideSheet'
import { RunReportStepIndicator } from '../../components/RunReportStepIndicator'
import { SelectFocusedTopicsSheet } from '../../components/SelectFocusedTopicsSheet'

const DEFAULT_BEHAVIOR_KEYS = [
  'threats',
  'violence',
  'weapon',
  'harassment',
  'prejudice',
  'disparaging',
  'selfHarm',
  'suggestiveContent',
  'fraud',
  'profanity',
  'polarizingContent',
  'drugs',
  'nudity',
  'alcohol',
] as const

export const BehaviorFlags = () => {
  useRunReportGuard(ERunReportStep.BehaviorFlags)
  const { current, total } = useRunReportWizardStep(
    ERunReportStep.BehaviorFlags,
  )
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const [openSheet, setOpenSheet] = useState<
    'behaviorDetection' | 'focusedTopicsGuide' | 'selectTopics' | null
  >(null)

  const form = useForm<behaviorFlagsSchemaType>({
    resolver: zodResolver(behaviorFlagsSchema),
    defaultValues: {
      topics: slice.firstStep.topics ?? [],
    },
    mode: 'onChange',
  })

  const { handleSubmit, setValue, watch } = form

  useEffect(() => {
    const topics = slice.firstStep.topics
    if (topics && topics.length > 0) {
      setValue('topics', topics, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    }
  }, [slice.firstStep.topics, setValue])

  const selectedTopics = watch('topics') ?? []

  const handleSelectTopicsSave = (next: string[]) => {
    setValue('topics', next, { shouldValidate: true, shouldDirty: true })
  }

  const handleBack = () =>
    navigate(
      slice.selectSearchType.searchType === ESearchType.Company
        ? AppRoutes.runReportCompanyForm
        : AppRoutes.runReportAddExtraDetails,
    )

  const handleSkip = () => {
    dispatch(setFirstStep({ topics: [] }))
    navigate(AppRoutes.runReportSubmit)
  }

  const onSubmit = (data: behaviorFlagsSchemaType) => {
    dispatch(setFirstStep({ topics: data.topics }))
    navigate(AppRoutes.runReportSubmit)
  }

  const progressPercent = (current / total) * 100

  const renderDefaultsCard = () => (
    <div className="flex flex-col gap-6 rounded-2xl bg-baseColorHighlight p-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:p-6">
      <div className="flex flex-col gap-3">
        <p className="text-xl font-medium text-neutralColor">
          {t('dashboard.runReport.behaviorFlags.defaultsCardTitle')}
        </p>
        <button
          type="button"
          onClick={() => setOpenSheet('behaviorDetection')}
          className="flex items-center gap-2 text-left text-base text-dynamicBlue hover:underline"
        >
          {t('dashboard.runReport.behaviorFlags.behaviorDetectionGuide')}
          <ArrowRight size="16" color={weCheckPalette.dynamicBlue} />
        </button>
        <p className="text-base text-neutralColor80">
          <Trans
            i18nKey="dashboard.runReport.behaviorFlags.defaultsCardDescription"
            components={{ b: <strong className="font-bold" /> }}
          />
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        {DEFAULT_BEHAVIOR_KEYS.map((key) => (
          <Chip
            key={key}
            variant={ChipVariant.Blue}
            size={ChipSize.Large}
            text={t(`dashboard.newSearch.focusedTopics.${key}`)}
          />
        ))}
      </div>
    </div>
  )

  const renderFocusedCard = () => (
    <div className="flex flex-col gap-6 rounded-2xl bg-baseColorHighlight p-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:p-6">
      <div className="flex flex-col gap-3">
        <p className="text-xl font-medium text-neutralColor">
          {t('dashboard.runReport.behaviorFlags.focusedCardTitle')}
        </p>
        <button
          type="button"
          onClick={() => setOpenSheet('focusedTopicsGuide')}
          className="flex items-center gap-2 text-left text-base text-dynamicBlue hover:underline"
        >
          {t('dashboard.runReport.behaviorFlags.focusedTopicsGuide')}
          <ArrowRight size="16" color={weCheckPalette.dynamicBlue} />
        </button>
        <p className="text-base text-neutralColor80">
          <Trans
            i18nKey="dashboard.runReport.behaviorFlags.focusedCardDescription"
            components={{ b: <strong className="font-bold" /> }}
          />
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-6">
        {selectedTopics.map((topicName) => (
          <Chip
            key={topicName}
            variant={ChipVariant.SelectedTopic}
            size={ChipSize.Large}
            text={topicName}
          />
        ))}
        <Button
          type="button"
          variant="secondary"
          className="gap-1.5"
          onClick={() => setOpenSheet('selectTopics')}
        >
          {t('dashboard.runReport.behaviorFlags.selectFocusedTopics')}
          <Add size="16" color="currentColor" />
        </Button>
      </div>
    </div>
  )

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      onClickBack={handleBack}
      hideHeaderOnMobile
      contentClassName="p-0"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex h-[calc(100dvh-3.5rem)] min-h-0 flex-col lg:h-full lg:flex-row lg:gap-6 lg:overflow-hidden lg:p-8"
        >
          <div className="hidden h-full flex-col lg:flex lg:flex-1">
            <div className="flex flex-1 flex-col justify-center gap-3 px-3">
              <RunReportStepIndicator current={current} total={total} />
              <h2 className="text-[32px] font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.behaviorFlags.title')}
              </h2>
              <p className="text-base text-neutralColor">
                {t('dashboard.runReport.behaviorFlags.subtitle')}
              </p>

              <div className="mt-2 h-5 w-full overflow-hidden rounded-full bg-neutralColor30">
                <div
                  className="h-full rounded-full bg-primaryColor transition-[width] duration-200"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="mt-2 flex gap-4">
                <Button type="button" variant="secondary" onClick={handleSkip}>
                  {t('dashboard.runReport.behaviorFlags.skip')}
                </Button>
                <Button type="submit">
                  {t('dashboard.runReport.behaviorFlags.confirm')}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-6 lg:h-full lg:gap-8 lg:overflow-hidden lg:p-0">
            {renderDefaultsCard()}
            {renderFocusedCard()}
          </div>

          <div className="flex shrink-0 flex-col gap-3 bg-baseColor px-4 pb-4 pt-3 lg:hidden">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.behaviorFlags.title')}
              </h2>
              <p className="text-sm text-neutralColor">
                {t('dashboard.runReport.behaviorFlags.subtitle')}
              </p>
            </div>
            <div className="flex gap-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleSkip}
                className="flex-1"
              >
                {t('dashboard.runReport.behaviorFlags.skip')}
              </Button>
              <Button type="submit" className="flex-1">
                {t('dashboard.runReport.behaviorFlags.confirm')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>

      <BehaviorDetectionGuideSheet
        open={openSheet === 'behaviorDetection'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
      />
      <FocusedTopicsGuideSheet
        open={openSheet === 'focusedTopicsGuide'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
      />
      <SelectFocusedTopicsSheet
        open={openSheet === 'selectTopics'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        value={selectedTopics}
        onSave={handleSelectTopicsSave}
      />
    </WeScreen>
  )
}
